import { LogOut, Menu, Monitor, Moon, Search, Sun } from "lucide-react";
import { Fragment, useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";

import type { Me } from "../../api/types";
import { Notifications } from "../../components/Notifications";
import { Button } from "../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { useTheme, type ThemePreference } from "../../design/theme";
import { pageTitle, useBranding } from "../branding";
import { breadcrumbs, visibleNav } from "./nav";
import { levelLabel } from "./Sidebar";

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
export const PALETTE_SHORTCUT = isMac ? "⌘K" : "Ctrl K";

/** Top bar (US-137): menu button below 1024 px, breadcrumbs, the persistent ask bar (REQ-042), notifications, account. */
export function Topbar({ me, onMenu, onPalette, onSignOut }: { me: Me; onMenu: () => void; onPalette: () => void; onSignOut: () => void }) {
  return (
    <header className="sticky top-0 z-30 flex min-h-topbar-height flex-wrap items-center gap-x-4 gap-y-2 border-b border-line bg-surface-raised px-4 py-2 lg:px-6">
      <Button variant="ghost" size="icon" className="lg:hidden" onClick={onMenu} aria-label="Open navigation">
        <Menu aria-hidden className="size-5" />
      </Button>
      <Breadcrumbs me={me} />
      <AskBar onPalette={onPalette} />
      <div className="flex items-center gap-1">
        <Notifications />
        <AccountMenu me={me} onSignOut={onSignOut} />
      </div>
    </header>
  );
}

function Breadcrumbs({ me }: { me: Me }) {
  const { pathname } = useLocation();
  const crumbs = breadcrumbs(visibleNav(me), pathname);
  const title = crumbs[crumbs.length - 1]!.label;
  const brand = useBranding();
  useEffect(() => {
    document.title = pageTitle(title, brand);
  }, [title, brand]);
  return (
    <nav aria-label="Breadcrumb" className="hidden min-w-0 flex-[1_1_12rem] sm:block">
      <ol className="flex items-center gap-1.5 text-ink-muted">
        {crumbs.map((c, i) => {
          const last = i === crumbs.length - 1;
          return (
            <Fragment key={`${c.label}-${i}`}>
              {i > 0 && <li aria-hidden>/</li>}
              <li className={last ? "truncate font-medium text-ink" : "whitespace-nowrap"} aria-current={last ? "page" : undefined}>
                {c.to && !last ? <Link className="hover:text-ink hover:underline focus-visible:focus-ring" to={c.to}>{c.label}</Link> : c.label}
              </li>
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}

/** The persistent ask bar (REQ-042): Enter asks; the shortcut chip opens the command palette. */
function AskBar({ onPalette }: { onPalette: () => void }) {
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  const [question, setQuestion] = useState("");
  const navigate = useNavigate();
  // Clear the bar once the question has been handed to Ask.
  useEffect(() => {
    if (pathname === "/ask" && params.get("q")) setQuestion("");
  }, [pathname, params]);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (question.trim()) navigate(`/ask?q=${encodeURIComponent(question.trim())}`);
  };
  return (
    <form role="search" onSubmit={submit}
      className="flex h-control-lg min-w-0 flex-[3_1_20rem] items-center gap-2 rounded-md border border-line-strong bg-surface pl-3 pr-1 focus-within:focus-ring lg:max-w-[560px]">
      <Search aria-hidden className="size-4 shrink-0 text-ink-muted" />
      <label htmlFor="ask" className="sr-only">Ask anything about your data</label>
      <input
        id="ask"
        type="search"
        placeholder="Ask anything about your data"
        value={question}
        maxLength={2000}
        onChange={(e) => setQuestion(e.target.value)}
        className="h-full min-w-0 flex-1 bg-transparent text-body text-ink outline-none placeholder:text-ink-disabled"
      />
      <button type="button" onClick={onPalette} aria-label="Open command palette"
        className="hidden rounded-sm border border-line px-1 font-mono text-[11px] text-ink-muted hover:text-ink focus-visible:focus-ring sm:block">
        {PALETTE_SHORTCUT}
      </button>
      <Button type="submit" size="sm" variant="ghost">Ask</Button>
    </form>
  );
}

const THEMES: { value: ThemePreference; label: string; icon: typeof Sun }[] = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "Match my system", icon: Monitor },
];

const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join("") || "?";

/** User menu: name and level, theme (saved per user, US-134) and sign out. */
function AccountMenu({ me, onSignOut }: { me: Me; onSignOut: () => void }) {
  const { preference, setPreference } = useTheme();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Account menu">
          <span aria-hidden className="grid size-7 place-items-center rounded-full bg-accent-soft text-small font-semibold text-accent">{initials(me.display_name)}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <div className="grid px-2 py-1.5">
          <span className="font-medium text-ink">{me.display_name}</span>
          <span className="text-small text-ink-muted">{levelLabel(me)}</span>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Theme</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={preference} onValueChange={(v) => setPreference(v as ThemePreference)}>
          {THEMES.map((t) => (
            <DropdownMenuRadioItem key={t.value} value={t.value}>
              <t.icon aria-hidden />
              {t.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onSignOut}>
          <LogOut aria-hidden />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
